import Button from "atoms/buttons/button";
import DataTable from "organisms/data-tables/data-table";
import GroupInviteCard from "organisms/group-invite-card/group-invite-card";
import GroupRecord from "models/view-models/group-record";
import GroupService from "utilities/services/groups/group-service";
import Loader from "atoms/loaders/loader";
import PageNavigationMenu from "molecules/page-navigation/page-navigation-menu";
import Paragraph from "atoms/typography/paragraph";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import SearchForm from "molecules/forms/search-form";
import StringUtils from "utilities/string-utils";
import TeamManagementTeamNameField from "pages/account/tabs/tab-panels/team-management-team-name-field";
import Tooltip, { TooltipPlacement } from "molecules/tooltips/tooltip";
import UserRoleGroupRecord from "models/view-models/user-role-group-record";
import UserRoleGroupRow from "organisms/user-role-groups/user-role-group-row";
import UserRoleGroupService from "utilities/services/user-role-groups/user-role-group-service";
import useBreakpoint, {
    BreakpointComparer,
} from "utilities/hooks/use-breakpoint";
import useFeatureFlags from "utilities/hooks/use-feature-flags";
import useNetworkInformation from "utilities/contexts/network-information/use-network-information";
import usePagedResults from "utilities/hooks/use-paged-results";
import useRefreshIdentity from "utilities/hooks/use-refresh-identity";
import useSearchText from "utilities/hooks/use-search-text";
import useUserRoleGroups from "utilities/hooks/domain/user-role-groups/use-user-role-groups";
import { usePollJob } from "utilities/hooks/domain/jobs/use-poll-job";
import { Breakpoints } from "utilities/enumerations/breakpoints";
import { ButtonSizes } from "atoms/constants/button-sizes";
import { ButtonStyles } from "atoms/constants/button-styles";
import AlertLevels from "constants/alert-levels";
import AlertBanner from "molecules/alerts/alert-banner";
import JobStatuses from "models/enumerations/jobs/job-statuses";
import { CollectionUtils } from "utilities/collection-utils";
import { DeleteService } from "utilities/services/service-factory";
import { MagicLinkUrlUtils } from "utilities/magic-link-url-utils";
import { ParagraphSizes } from "atoms/constants/paragraph-sizes";
import { RecordUtils, ResultRecord } from "@rsm-hcd/javascript-core";
import { SearchFormStyles } from "molecules/enums/search-form-style";
import { ToastManager } from "utilities/toast/toast-manager";
import { t } from "utilities/localization-utils";
import { useGlobalState } from "utilities/contexts/use-global-state-context";
import { useLocalization } from "utilities/hooks/use-localization";
import UserRoleRecord from "models/view-models/user-role-record";
import GroupSyncRecord from "models/view-models/group-sync-record";

// -----------------------------------------------------------------------------------------
// #region Interfaces
// -----------------------------------------------------------------------------------------

interface TeamManagementAreaProps {
    userRoleGroupToUse?: UserRoleGroupRecord;
    showInviteArea?: boolean;
    allowEdits?: boolean;
}

interface GroupSyncProgress {
    progressCount?: number;
    progressTotal?: number;
    syncedCount?: number;
    errorCount?: number;
    errors?: Array<string>;
}

// #endregion Interfaces

// -----------------------------------------------------------------------------------------
// #region Constants
// -----------------------------------------------------------------------------------------

const BASE_CLASS = "c-account-dashboard__team-management";
const BASE_CLASS_TABLE = `${BASE_CLASS}__table`;
const DEFAULT_TAKE_SIZE = 10;

// #endregion Constants

// -----------------------------------------------------------------------------------------
// #region Component
// -----------------------------------------------------------------------------------------

const TeamManagementArea: React.FC<TeamManagementAreaProps> = ({
    userRoleGroupToUse,
    showInviteArea = true,
    allowEdits = true,
}) => {
    // -----------------------------------------------------------------------------------------
    // #region State
    // -----------------------------------------------------------------------------------------

    const { globalState, setGlobalState } = useGlobalState();
    const { currentIdentity } = globalState;
    const [groupSynced, setGroupSynced] = useState(false);
    const [refreshGroupInvitesFlag, setRefreshGroupInvitesFlag] =
        useState(false);
    const [isGroupSyncing, setIsGroupSyncing] = useState(false);
    const [syncJobId, setSyncJobId] = useState<number | null>(null);
    const { isOnline } = useNetworkInformation();

    // #endregion State

    // -----------------------------------------------------------------------------------------
    // #region Computations
    // -----------------------------------------------------------------------------------------

    const currentUserRoleGroup = useMemo(
        () =>
            userRoleGroupToUse
                ? userRoleGroupToUse
                : (currentIdentity?.userRoles.find(
                      (ur) => ur.id === currentIdentity?.userLogin?.userRoleId
                  )?.userRoleGroup ?? new UserRoleGroupRecord()),
        [
            currentIdentity?.userLogin,
            currentIdentity?.userRoles,
            userRoleGroupToUse,
        ]
    );

    const currentUserRole =
        currentIdentity?.getCurrentUserRole() ?? new UserRoleRecord();

    const magicLinkUrlTemplate = MagicLinkUrlUtils.getMagicLinkUrlTemplate(
        globalState,
        currentUserRoleGroup
    );

    const isTablet = useBreakpoint(
        Breakpoints.SmallTablet,
        BreakpointComparer.MinWidth
    );

    // #endregion Computations

    // -----------------------------------------------------------------------------------------
    // #region Custom Hooks
    // -----------------------------------------------------------------------------------------

    const { delete: deleteApi } = UserRoleGroupService.useDelete();
    const { update: updateApi } = UserRoleGroupService.useUpdate();
    const { update: syncGroupApi } = GroupService.useSync();

    const {
        currentPage,
        numberOfPages,
        onPageLast,
        onPageNext,
        onPageSizeChange,
        onSelectPage,
        resetPagination,
        rowCount,
        setRowCount,
        skip,
        take,
    } = usePagedResults(DEFAULT_TAKE_SIZE);

    const {
        handleSearchClear,
        handleSearchTextChange,
        searchText,
        setSubmittedSearchText,
        submittedSearchText,
    } = useSearchText();

    const { t } = useLocalization();

    // Page errors + API service hooks
    const { refresh: refreshIdentity } = useRefreshIdentity();
    const {
        loaded,
        loading,
        refresh: refreshUserRoleGroups,
        resultObject: userRoleGroups,
        rowCount: rowCountFromApi,
    } = useUserRoleGroups({
        groupId: currentUserRoleGroup.groupId,
        includeUserRoleAndUser: true,
        searchText: submittedSearchText,
        skip: skip,
        take: take,
    });

    const { job: syncJob } = usePollJob(syncJobId);

    const groupCount = rowCountFromApi ?? 0;
    const showPaging = groupCount > DEFAULT_TAKE_SIZE;

    const showZeroSearchResults =
        !loading &&
        CollectionUtils.isEmpty(userRoleGroups) &&
        StringUtils.hasValue(searchText);

    const noResultsLabel = showZeroSearchResults
        ? t("noSearchResultsBanner-title")
        : "";
    const tryAgainMessage = showZeroSearchResults
        ? t("noSearchResultsBanner-subTitle")
        : "";

    const { bypassHermes } = useFeatureFlags();

    const syncProgress = useMemo((): GroupSyncProgress | null => {
        if (syncJob == null || syncJobId == null) return null;

        const rawDebugJson = syncJob?.debugJson;
        if (!StringUtils.hasValue(rawDebugJson)) {
            return { progressCount: 0, progressTotal: rowCountFromApi ?? 0 };
        }

        try {
            return JSON.parse(rawDebugJson as string) as GroupSyncProgress;
        } catch {
            return null;
        }
    }, [rowCountFromApi, syncJob, syncJobId]);

    // #endregion Custom Hooks

    // -----------------------------------------------------------------------------------------
    // #region Functions
    // -----------------------------------------------------------------------------------------

    const refreshUserRoleGroupData = () => {
        refreshUserRoleGroups();
    };

    const syncGroup = useCallback(
        async (force: boolean) => {
            const { group } = currentUserRoleGroup;
            if (group == null || group.id == null) {
                return;
            }

            try {
                setSyncJobId(null);
                setIsGroupSyncing(true);
                const { resultObject } = await syncGroupApi(
                    new GroupSyncRecord({ ...group.toObject(), jobId: null }),
                    {
                        id: group.id,
                    },
                    {
                        forceSync: force,
                    }
                );

                const jobId = resultObject?.jobId;

                if (jobId == null) {
                    refreshUserRoleGroups();
                    setRefreshGroupInvitesFlag((prev) => !prev);
                    setIsGroupSyncing(false);
                    return;
                }

                setSyncJobId(jobId);
            } catch (e) {
                const issueSyncingGroup = t("issueSyncingGroup");
                ToastManager.error(issueSyncingGroup);
                setIsGroupSyncing(false);
            }
        },
        [currentUserRoleGroup, refreshUserRoleGroups, syncGroupApi, t]
    );

    // #endregion Functions

    // -----------------------------------------------------------------------------------------
    // #region Side Effects
    // -----------------------------------------------------------------------------------------

    useEffect(() => {
        if (
            groupSynced ||
            currentUserRoleGroup == null ||
            currentUserRoleGroup.group == null
        ) {
            return;
        }

        setGroupSynced(true);
        syncGroup(false);
    }, [
        syncGroup,
        syncGroupApi,
        currentUserRoleGroup,
        groupSynced,
        refreshUserRoleGroups,
        refreshGroupInvitesFlag,
    ]);

    useEffect(() => {
        if (rowCountFromApi == null) {
            return;
        }

        setRowCount(rowCountFromApi);
    }, [rowCountFromApi, setRowCount]);

    useEffect(() => {
        resetPagination();
    }, [resetPagination, submittedSearchText]);

    useEffect(() => {
        if (
            syncJob == null ||
            syncJob.status == null ||
            syncJob.status === JobStatuses.InProgress
        ) {
            return;
        }

        refreshUserRoleGroups();
        setRefreshGroupInvitesFlag((prev) => !prev);
        setIsGroupSyncing(false);
        setSyncJobId(null);

        if (syncJob.status === JobStatuses.Errored) {
            const issueSyncingGroup = t("issueSyncingGroup");
            ToastManager.error(issueSyncingGroup);
        }
    }, [refreshUserRoleGroups, syncJob, t]);

    // #endregion Side Effects

    // -----------------------------------------------------------------------------------------
    // #region Event Handlers
    // -----------------------------------------------------------------------------------------

    const handleForceRefresh = async () => {
        await syncGroup(true);

        // Refresh identity to ensure underlying Group has current data (for example, totalLicenses or name may have changed)
        await refreshIdentity();
    };

    const handleTeamNameChange = (group: GroupRecord) =>
        setGlobalState((prev) => {
            if (group.id == null) {
                return prev;
            }

            return prev.updateTeamName(group.id, group.name);
        });

    const handleUpdate = async (userRoleGroup: UserRoleGroupRecord) => {
        try {
            await updateApi(
                userRoleGroup.with({ isAdmin: !userRoleGroup.isAdmin }),
                { id: userRoleGroup.id! }
            );
        } catch (error) {
            if (
                RecordUtils.isRecord(error, ResultRecord) &&
                error.hasErrors()
            ) {
                ToastManager.error(error.listErrorMessages());
                return;
            }

            const errorMessage = t("errors-actionResource", {
                action: t("updating"),
                resource: t("user"),
            });
            ToastManager.error(errorMessage);
            return;
        }

        refreshUserRoleGroups();
        ToastManager.success(t("updatedItem", { item: t("userRole") }));
    };

    // #endregion Event Handlers

    // -----------------------------------------------------------------------------------------
    // #region Render
    // -----------------------------------------------------------------------------------------

    const membersLoading = loading || isGroupSyncing;
    const groupCountLabel = loaded ? `${groupCount} ` : "-- ";
    const searchPlaceholder = `${t("searchby-nameOrEmail")}...`;
    const refreshButtonStyle =
        isTablet && bypassHermes
            ? ButtonStyles.Tertiary
            : ButtonStyles.Secondary;

    return (
        <div className={BASE_CLASS}>
            {allowEdits && (
                <TeamManagementTeamNameField
                    userRoleGroup={currentUserRoleGroup}
                    group={currentUserRoleGroup?.group}
                    onChange={handleTeamNameChange}
                />
            )}
            {currentUserRoleGroup?.isAdmin && showInviteArea && (
                <GroupInviteCard
                    existingUserRoleGroupCount={groupCount}
                    group={currentUserRoleGroup?.group!}
                    magicLinkUrlTemplate={encodeURI(magicLinkUrlTemplate)}
                    refreshInvites={refreshGroupInvitesFlag}
                />
            )}
            <div className={`${BASE_CLASS}__team-members`}>
                <div className={`${BASE_CLASS}__team-members__title`}>
                    {groupCountLabel}
                    {t(
                        Math.abs(groupCount) > 1
                            ? "team-member_plural"
                            : "team-member"
                    )}
                    <Tooltip
                        placement={TooltipPlacement.Right}
                        content={t("bypassHermes-feature-unavailable-tooltip")}
                        disabled={!bypassHermes}
                        durationInMs={0}
                        hideOnClick={true}>
                        <div
                            className={`${BASE_CLASS}__team-members__title__button-wrapper-for-tooltip`}>
                            <Button
                                disabled={!isOnline || bypassHermes}
                                onClick={handleForceRefresh}
                                size={ButtonSizes.Small}
                                style={refreshButtonStyle}>
                                {t("refresh")}
                            </Button>
                        </div>
                    </Tooltip>
                </div>
                <div className={`${BASE_CLASS}__team-members__search`}>
                    {isGroupSyncing &&
                        syncProgress != null &&
                        _renderGroupSyncProgressBanner(syncProgress)}
                    {currentUserRoleGroup.isAdmin && (
                        <SearchForm
                            disabled={!isOnline}
                            onClear={handleSearchClear}
                            onSearchClick={(e) =>
                                setSubmittedSearchText(searchText)
                            }
                            onSearchTextChange={handleSearchTextChange}
                            placeholder={searchPlaceholder}
                            searchText={searchText}
                            style={SearchFormStyles.Tertiary}
                        />
                    )}
                </div>
                {membersLoading && (
                    <Loader
                        accessibleText={t("loadingItem", {
                            item: t("team-member_plural"),
                        })}
                    />
                )}
                {!membersLoading && loaded && (
                    <React.Fragment>
                        <DataTable>
                            <thead className={`${BASE_CLASS_TABLE}`}>
                                <tr>
                                    <th
                                        className={`${BASE_CLASS_TABLE}__team-members`}>
                                        Team Members
                                    </th>
                                    <th
                                        className={`${BASE_CLASS_TABLE}__email`}>
                                        Email
                                    </th>
                                    <th
                                        className={`${BASE_CLASS_TABLE}__last-active`}>
                                        Last Active
                                    </th>
                                    <th className={`${BASE_CLASS_TABLE}__type`}>
                                        Type
                                    </th>
                                    <th className={"-collapse"}>
                                        <span className="sr-only">
                                            {t("edit")}
                                        </span>
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {userRoleGroups?.map(
                                    (userRoleGroup: UserRoleGroupRecord) => (
                                        <UserRoleGroupRow
                                            currentUserRoleGroup={
                                                currentUserRoleGroup
                                            }
                                            currentUserRole={currentUserRole}
                                            key={userRoleGroup.id}
                                            onDelete={() => {
                                                handleDeleteUserRoleGroup(
                                                    deleteApi,
                                                    refreshUserRoleGroupData,
                                                    userRoleGroup
                                                );
                                            }}
                                            onUpdate={() =>
                                                handleUpdate(userRoleGroup)
                                            }
                                            userRoleGroup={userRoleGroup}
                                            allowEdits={allowEdits}
                                        />
                                    )
                                )}

                                {showZeroSearchResults && (
                                    <div
                                        className={`c-no-results-banner -secondary`}>
                                        <Paragraph size={ParagraphSizes.Large}>
                                            {noResultsLabel}
                                        </Paragraph>
                                        <Paragraph size={ParagraphSizes.Small}>
                                            {tryAgainMessage}
                                        </Paragraph>
                                    </div>
                                )}
                            </tbody>
                        </DataTable>

                        {showPaging && (
                            <PageNavigationMenu
                                currentPage={currentPage}
                                numberOfPages={numberOfPages}
                                onNavigateBack={onPageLast}
                                onNavigateForward={onPageNext}
                                onPageSizeChange={onPageSizeChange}
                                onSelectPage={onSelectPage}
                                pageSize={take}
                                resultTotal={rowCount}
                            />
                        )}
                    </React.Fragment>
                )}
            </div>
        </div>
    );

    // #endregion Render
};

// #endregion Component

// -----------------------------------------------------------------------------------------
// #region Functions
// -----------------------------------------------------------------------------------------

const _renderGroupSyncProgressBanner = (
    syncProgress: GroupSyncProgress
): React.ReactNode => {
    const progressPercent = syncProgress.progressTotal
        ? Math.round(
              (syncProgress.progressCount! / syncProgress.progressTotal) * 100
          )
        : 0;
    const errorText =
        syncProgress.errorCount && syncProgress.errorCount > 0
            ? ` (${syncProgress.errorCount} errors)`
            : "";

    return (
        <AlertBanner alertLevel={AlertLevels.Info}>
            {`${t("syncingMembers")} (${syncProgress.progressCount}/${syncProgress.progressTotal} - ${progressPercent}%)${errorText} `}
        </AlertBanner>
    );
};

const getSuccessMessage = (userRoleGroup: UserRoleGroupRecord): string => {
    const user = userRoleGroup.userRole?.user;
    if (user == null) {
        return t("successfullyRemovedFromTeam", { user: t("user") });
    }

    return t("successfullyRemovedFromTeam", {
        user: user.getFirstAndLastName(),
    });
};

const handleDeleteUserRoleGroup = async (
    deleteApi: DeleteService,
    refreshUserRoleGroups: () => void,
    userRoleGroup: UserRoleGroupRecord
) => {
    try {
        await deleteApi(userRoleGroup.id ?? 0);
    } catch (error) {
        // If there are specific errors from the API, lets use those.
        if (error instanceof ResultRecord && error.hasErrors()) {
            ToastManager.error(error.listErrorMessages());
            return;
        }

        const errorMessage = t("errors-actionResource", {
            action: t("removing"),
            resource: t("user"),
        });
        ToastManager.error(errorMessage);
        return;
    }

    refreshUserRoleGroups();
    ToastManager.success(getSuccessMessage(userRoleGroup));
};

// #endregion Functions

// -----------------------------------------------------------------------------------------
// #region Exports
// -----------------------------------------------------------------------------------------

export default TeamManagementArea;

// #endregion Exports
